import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import MicIcon from "@material-ui/icons/Mic";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import AudioRecorder from "../AudioRecorder";

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Typography,
  Chip,
  Divider,
  ClickAwayListener,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  mediaContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  mediaOptions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  mediaInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius
  },
  existingMediaActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  mediaPreview: {
    maxWidth: '100%',
    maxHeight: 200,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1)
  },
  emojiBox: {
    position: "relative",
    zIndex: 1,
  },
  emojiPickerPopup: {
    position: "absolute",
    top: 0,
    zIndex: 10,
  },
}));

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required("Obrigatório"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const messageInputRef = useRef();

  const initialState = {
    shortcode: "",
    message: "",
    geral: true,
    visao: true,
    isOficial: false,
    status: "APPROVED",
    language: "pt_BR",
    category: "",
    metaID: "",
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mediaMode, setMediaMode] = useState(null);
  const [isEditingMedia, setIsEditingMedia] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const attachmentFile = useRef(null);

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;
        const { data } = await api.get(`/quick-messages/${quickemessageId}`);
        setQuickemessage((prev) => ({ ...prev, ...data }));
        setIsEditingMedia(false);
        setMediaMode(null);
      })();
    } catch (err) {
      toastError(err);
    }
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
      setAudioBlob(null);
      setMediaMode('file');
      setIsEditingMedia(true);
    }
  };

  const handleAudioRecorded = (blob) => {
    setAudioBlob(blob);
    setAttachment(null);
    setMediaMode('audio');
    setIsEditingMedia(true);
  };

  const handleAudioDeleted = () => {
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleCancelEdit = () => {
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    if (attachmentFile.current) attachmentFile.current.value = null;
  };

  const handleEditExistingMedia = () => {
    setIsEditingMedia(true);
    setMediaMode('edit');
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    const cleanPath = filepath.split('?')[0].split('#')[0];
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  // ── Salvar ──
  const handleSaveQuickeMessage = async (values) => {
    const quickemessageData = {
      ...values,
      isOficial: false,
      isMedia: !!(attachment || audioBlob || (quickemessage.mediaPath && !isEditingMedia)),
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
          ? getBasename(values.mediaPath).replace(/ /g, "_")
          : null,
    };

    try {
      let quickMessageRecord;
      if (quickemessageId) {
        quickMessageRecord = await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        quickMessageRecord = { data };
      }

      const messageId = quickemessageId || quickMessageRecord.data.id;

      if (isEditingMedia && quickemessage.mediaPath) {
        await api.delete(`/quick-messages/${messageId}/media-upload`);
      }
      if (attachment) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage");
        formData.append("file", attachment);
        await api.post(`/quick-messages/${messageId}/media-upload`, formData);
      }
      if (audioBlob) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage");
        formData.append("audio", audioBlob, `audio_${Date.now()}.webm`);
        await api.post(`/quick-messages/${messageId}/audio-upload`, formData);
      }

      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") reload();
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }
    if (audioBlob) setAudioBlob(null);
    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({ ...prev, mediaPath: null, mediaName: null, mediaType: null }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload === "function") reload();
    }
    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;
    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);
    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const handleAddEmoji = async (emoji, setValueFunc) => {
    await handleClickMsgVar(emoji.native, setValueFunc);
    setShowEmoji(false);
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'audio': return '🎵';
      case 'image': return '🖼️';
      case 'video': return '🎥';
      default: return '📎';
    }
  };

  const getMediaPreview = (qm) => {
    if (!qm.mediaPath) return null;
    const mediaUrl = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/public/company${user.companyId}/quickMessage/${qm.mediaName}`;
    if (qm.mediaType === 'image') {
      return <img src={mediaUrl} alt={qm.mediaName} className={classes.mediaPreview} onError={(e) => { e.target.style.display = 'none'; }} />;
    }
    if (qm.mediaType === 'video') {
      return <video controls className={classes.mediaPreview} src={mediaUrl}>Seu navegador não suporta vídeo.</video>;
    }
    return null;
  };

  const hasNewMedia = attachment || audioBlob;
  const hasExistingMedia = quickemessage.mediaPath && !isEditingMedia;
  const hasAnyMedia = hasNewMedia || hasExistingMedia;

  const dialogTitle = quickemessageId
    ? i18n.t("quickMessages.dialog.edit")
    : i18n.t("quickMessages.dialog.add");

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{dialogTitle}</DialogTitle>

        <Formik
          initialValues={{ ...quickemessage, isOficial: false }}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
            {({ touched, errors, isSubmitting, setFieldValue, values }) => {
              const isDisabled = quickemessageId && values.visao && !values.geral && values.userId !== user.id;
              return (
                <Form>
                  <div style={{ display: "none" }}>
                    <input
                      type="file"
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                      ref={attachmentFile}
                      onChange={handleAttachmentFile}
                    />
                  </div>
                  <DialogContent dividers>
                    <Grid spacing={2} container>
                      <Grid xs={12} item>
                        <Field
                          as={TextField}
                          autoFocus
                          label={i18n.t("quickMessages.dialog.shortcode")}
                          name="shortcode"
                          disabled={isDisabled}
                          error={touched.shortcode && Boolean(errors.shortcode)}
                          helperText={touched.shortcode && errors.shortcode}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid xs={12} item>
                        <Field
                          as={TextField}
                          label={i18n.t("quickMessages.dialog.message")}
                          name="message"
                          inputRef={messageInputRef}
                          error={touched.message && Boolean(errors.message)}
                          helperText={touched.message && errors.message}
                          variant="outlined"
                          margin="dense"
                          disabled={isDisabled}
                          multiline={true}
                          rows={7}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box className={classes.emojiBox}>
                          <IconButton
                            size="small"
                            aria-label="emojiPicker"
                            disabled={isSubmitting || isDisabled}
                            onClick={() => setShowEmoji((prev) => !prev)}
                          >
                            <InsertEmoticonIcon />
                          </IconButton>
                          {showEmoji && (
                            <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                              <div className={classes.emojiPickerPopup}>
                                <Picker
                                  perLine={16}
                                  theme="dark"
                                  i18n={i18n}
                                  showPreview={true}
                                  showSkinTones={false}
                                  onSelect={(emoji) => handleAddEmoji(emoji, setFieldValue)}
                                />
                              </div>
                            </ClickAwayListener>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <MessageVariablesPicker
                          disabled={isSubmitting || isDisabled}
                          showSchedulingVars={true}
                          onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                        />
                      </Grid>

                      {/* Mídia */}
                      <Grid xs={12} item>
                        <Box className={classes.mediaContainer}>
                          <Typography variant="h6" gutterBottom>Anexar Mídia</Typography>
                          {hasExistingMedia && (
                            <>
                              <Box className={classes.mediaInfo}>
                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                  <span>{getMediaTypeIcon(quickemessage.mediaType)}</span>
                                  <Typography variant="body2">{quickemessage.mediaName}</Typography>
                                  {quickemessage.mediaType && <Chip size="small" label={quickemessage.mediaType} variant="outlined" />}
                                </Box>
                                <Box className={classes.existingMediaActions}>
                                  <IconButton onClick={handleEditExistingMedia} color="primary" size="small" disabled={isDisabled}><EditIcon /></IconButton>
                                  <IconButton onClick={() => setConfirmationOpen(true)} color="secondary" size="small" disabled={isDisabled}><DeleteOutlineIcon /></IconButton>
                                </Box>
                              </Box>
                              {getMediaPreview(quickemessage)}
                            </>
                          )}
                          {attachment && (
                            <Box className={classes.mediaInfo}>
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <AttachFileIcon />
                                <Typography variant="body2">{attachment.name}</Typography>
                                <Chip size="small" label="Novo Arquivo" color="primary" />
                              </Box>
                              <IconButton onClick={() => { setAttachment(null); setMediaMode(null); setIsEditingMedia(false); if (attachmentFile.current) attachmentFile.current.value = null; }} color="secondary" size="small"><DeleteOutlineIcon /></IconButton>
                            </Box>
                          )}
                          {audioBlob && (
                            <Box className={classes.mediaInfo}>
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <MicIcon />
                                <Typography variant="body2">Novo áudio gravado</Typography>
                                <Chip size="small" label="Novo Áudio" color="secondary" />
                              </Box>
                              <IconButton onClick={handleAudioDeleted} color="secondary" size="small"><DeleteOutlineIcon /></IconButton>
                            </Box>
                          )}
                          {(!hasAnyMedia || isEditingMedia) && (
                            <>
                              {isEditingMedia && hasExistingMedia && (
                                <>
                                  <Divider />
                                  <Typography variant="body2" color="textSecondary" align="center">Escolha uma nova mídia para substituir:</Typography>
                                </>
                              )}
                              {!hasAnyMedia && <Typography variant="body2" color="textSecondary" align="center">Escolha uma opção para anexar mídia:</Typography>}
                              <Box className={classes.mediaOptions}>
                                <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => attachmentFile.current.click()} disabled={isSubmitting || isDisabled}>Anexar Arquivo</Button>
                                <Button variant="outlined" startIcon={<MicIcon />} onClick={() => setMediaMode('audio')} disabled={isSubmitting || isDisabled}>Gravar Áudio</Button>
                              </Box>
                              <Typography variant="caption" color="textSecondary" align="center" display="block" style={{ marginTop: 6 }}>
                                Tamanho máximo: <strong>16 MB</strong> para arquivos · <strong>16 MB</strong> para áudios
                              </Typography>
                              {isEditingMedia && (
                                <Box style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                                  <Button variant="outlined" color="secondary" onClick={handleCancelEdit} disabled={isSubmitting}>Cancelar Edição</Button>
                                </Box>
                              )}
                            </>
                          )}
                          {mediaMode === 'audio' && !audioBlob && (
                            <AudioRecorder onAudioRecorded={handleAudioRecorded} onAudioDeleted={handleAudioDeleted} disabled={isSubmitting || isDisabled} />
                          )}
                        </Box>
                      </Grid>

                      {/* Visibilidade */}
                      <Grid xs={12} item>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel id="visao-label">{i18n.t("quickMessages.dialog.visao")}</InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("quickMessages.dialog.visao")}
                            labelId="visao-label"
                            name="visao"
                            disabled={isDisabled}
                            onChange={(e) => setFieldValue("visao", e.target.value === "true")}
                            value={values.visao ? "true" : "false"}
                          >
                            <MenuItem value="true">{i18n.t("announcements.active")}</MenuItem>
                            <MenuItem value="false">{i18n.t("announcements.inactive")}</MenuItem>
                          </Field>
                        </FormControl>
                        {values.visao === true && (
                          <FormControl variant="outlined" margin="dense" fullWidth>
                            <InputLabel id="geral-label">{i18n.t("quickMessages.dialog.geral")}</InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("quickMessages.dialog.geral")}
                              labelId="geral-label"
                              name="geral"
                              disabled={isDisabled}
                              value={values.geral ? "true" : "false"}
                            >
                              <MenuItem value="true">{i18n.t("announcements.active")}</MenuItem>
                              <MenuItem value="false">{i18n.t("announcements.inactive")}</MenuItem>
                            </Field>
                          </FormControl>
                        )}
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                      {i18n.t("quickMessages.buttons.cancel")}
                    </Button>
                    <Button type="submit" color="primary" disabled={isSubmitting || isDisabled} variant="contained" className={classes.btnWrapper}>
                      {quickemessageId ? i18n.t("quickMessages.buttons.edit") : i18n.t("quickMessages.buttons.add")}
                      {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                    </Button>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
      </Dialog>
    </div>
  );
};

export default QuickMessageDialog;
